import axios from 'axios';
import router from '@/router';

const api = axios.create({
  baseURL: (process.env.VUE_APP_API_URL || '') + '/v1',
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue  = [];

export function clearApiAuthState() {
  isRefreshing = false;
  failedQueue = [];

  if (api.defaults?.headers?.common?.Authorization) {
    delete api.defaults.headers.common.Authorization;
  }
}

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

function isPublicCheckoutRoute(url = '') {
  const normalized = String(url || '').toLowerCase();
  return (
    normalized.includes('/payments/') ||
    normalized.includes('/guests') ||
    normalized.includes('/config/fees') ||
    normalized.includes('/compliance/accept')
  );
}

function isAuthRoute(url = '') {
  const normalized = String(url || '').toLowerCase();
  return (
    normalized.includes('/auth/login') ||
    normalized.includes('/auth/forgot-password') ||
    normalized.includes('/auth/logout')
  );
}

api.interceptors.request.use(async (config) => {
  try {
    const { useAuthStore } = await import('@/stores/authStore');
    const authStore = useAuthStore();

    if (!config.headers) {
      config.headers = {};
    }

    if (authStore.token) {
      config.headers.Authorization = `Bearer ${authStore.token}`;
    } else if (config.headers.Authorization) {
      delete config.headers.Authorization;
    }
  } catch {
    // store ainda não inicializada
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url || '';

    if (error.response?.status === 401 && isPublicCheckoutRoute(requestUrl)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && isAuthRoute(requestUrl)) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      isRefreshing = true;

      try {
        const refreshBaseUrl = (process.env.VUE_APP_API_URL || '') + '/v1';
        const refreshResponse = await axios.post(`${refreshBaseUrl}/auth/refresh`, {}, {
          withCredentials: true,
        });

        const newToken = refreshResponse.data.token;

        const { useAuthStore } = await import('@/stores/authStore');
        const authStore = useAuthStore();
        authStore.setToken(newToken);

        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);

        const currentRoute = router.currentRoute?.value;
        const routeRequiresAuth = !!currentRoute?.meta?.requiresAuth;

        if (routeRequiresAuth) {
          const { useAuthStore } = await import('@/stores/authStore');
          const authStore = useAuthStore();
          authStore.logout();
          router.push('/login');
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;